import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Truck, Package, Zap, Layers, Shield, ArrowUpDown, ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useInitScrollAnimations } from '../hooks/useScrollAnimation';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { Truck, Package, Zap, Layers, Shield, ArrowUpDown };

const TRUCK_IMAGES = {
  hero: '/images/truck-hero.jpg',
  snow: '/images/truck-snow.jpg',
  port: '/images/truck-port.jpg'
};

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const { language } = useSiteContent();

  // Default translations
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'НАШИТЕ УСЛУГИ',
    heroTitle: 'Цялостни',
    heroTitleHighlight: 'транспортни решения',
    heroDescription: 'От международни премествания до експресни доставки, предлагаме пълна гама транспортни услуги, съобразени с вашите специфични нужди.',
    requestQuote: 'Заявете оферта',
    ctaTitle: 'Нуждаете се от персонализирано решение?',
    ctaText: 'Свържете се с нашия екип, за да обсъдим вашите специфични изисквания.',
    getQuote: 'Получете оферта',
    talkToExpert: 'Говорете с експерт',
    serviceDetails: {
      'international-moving': { 
        fullDescription: 'Tetralog Ltd. предоставя комплексни услуги за международно преместване на офиси, домове и бизнеси.', 
        features: ['Офис и домашни премествания', 'Бизнес и корпоративни премествания', 'Багаж и лични вещи', 'XXL преместване за големи предмети', 'Професионални услуги за опаковане', 'Помощ при товарене и разтоварване', 'Налични складови решения', 'Доставка от врата до врата']
      },
      'personal-belongings': { 
        fullDescription: 'Специализираме се в транспортирането на лични вещи в цяла Европа с грижа и прецизност.', 
        features: ['Транспорт на велосипеди', 'Домакински уреди (малки и големи)', 'Потребителска електроника', 'Мотоциклети и скутери', 'Опции за консолидация на склад', 'Сигурно опаковане включено', 'Проследяване в реално време', 'Застрахователно покритие']
      },
      'express-transportation': { 
        fullDescription: 'Когато времето е критично, нашата услуга за експресен транспорт доставя в рамките на 24-40 часа до EU дестинации.', 
        features: ['24-40 часа доставка до EU', 'GPS проследяване в реално време', 'Приоритетно обработване', 'Гарантирани срокове за доставка', 'Налични директни маршрути', 'Актуализации чрез SMS/имейл', 'Опции за температурно чувствителни товари', '24/7 клиентска поддръжка']
      },
      'groupage-line': { 
        fullDescription: 'Нашата услуга Групажна линия предлага най-икономичния начин за транспортиране на товари в Европа.', 
        features: ['Икономична консолидация на товари', 'Маршрут България - Унгария - Австрия', 'Покритие на Словакия - Чехия', 'Германски дестинации', 'Редовни планирани заминавания', 'Гъвкави опции за вземане', 'Наличен складов склад', 'Отстъпки за редовни клиенти']
      },
      'specialized-cargo': { 
        fullDescription: 'Когато имате уникални изисквания за товар, доверете се на професионалистите за панаири, изложби и концерти.', 
        features: ['Оборудване за изложби и панаири', 'Концертни и сценични конфигурации', 'Обработка на крехки предмети', 'Специализирани модификации на превозни средства', 'ECO зона съвместим флот', 'Професионални екипи за опаковане', 'Помощ при монтаж на място', 'Времево критично планиране']
      },
      'loading-unloading': { 
        fullDescription: 'Нашите превозни средства са оборудвани с хидравлични платформи и електрокари, позволявайки гъвкаво товарене и разтоварване на всяко място.', 
        features: ['Превозни средства с хидравлична платформа', 'Наличност на електрокари', 'Не се изисква рампа', 'Обработка на тежки предмети', 'Възможност за товарене на мотоциклети', 'Мебели и оборудване', 'Индустриални стоки', 'Обучени екипи за товарене']
      }
    }
  } : {
    heroSubtitle: 'OUR SERVICES',
    heroTitle: 'Comprehensive',
    heroTitleHighlight: 'Transport Solutions',
    heroDescription: 'From international moving to express deliveries, we offer a full range of transport services tailored to meet your specific needs.',
    requestQuote: 'Request Quote',
    ctaTitle: 'Need a Custom Solution?',
    ctaText: 'Contact our team to discuss your specific requirements.',
    getQuote: 'Get a Quote',
    talkToExpert: 'Talk to an Expert',
    serviceDetails: {
      'international-moving': { 
        fullDescription: 'Tetralog Ltd. provides comprehensive international moving services for offices, homes, and businesses.', 
        features: ['Office and home relocations', 'Business and corporate moving', 'Luggage and personal items', 'XXL moving for large items', 'Optional professional packing services', 'Loading and unloading assistance', 'Storage solutions available', 'Door-to-door delivery']
      },
      'personal-belongings': { 
        fullDescription: 'We specialize in transporting personal belongings across Europe with care and precision.', 
        features: ['Bicycle transport', 'Home appliances (small and large)', 'Consumer electronics', 'Motorcycles and scooters', 'Storage consolidation options', 'Secure packaging included', 'Real-time tracking', 'Insurance coverage']
      },
      'express-transportation': { 
        fullDescription: 'When time is critical, our express transportation service delivers within 24-40 hours to EU destinations.', 
        features: ['24-40 hour delivery to EU', 'Real-time GPS tracking', 'Priority handling', 'Guaranteed delivery windows', 'Direct routes available', 'Status updates via SMS/email', 'Temperature-sensitive options', '24/7 customer support']
      },
      'groupage-line': { 
        fullDescription: 'Our Groupage Line service offers the most cost-efficient way to transport cargo across Europe.', 
        features: ['Cost-efficient cargo consolidation', 'Bulgaria - Hungary - Austria route', 'Slovakia - Czech Republic coverage', 'Germany destinations', 'Regular scheduled departures', 'Flexible pickup options', 'Warehouse storage available', 'Volume discounts for regulars']
      },
      'specialized-cargo': { 
        fullDescription: 'When dealing with unique cargo requirements, trust the professionals for fairs, exhibitions, and concerts.', 
        features: ['Exhibition and fair equipment', 'Concert and stage setup', 'Fragile item handling', 'Specialized vehicle modifications', 'ECO zone compliant fleet', 'Professional packing teams', 'On-site setup assistance', 'Time-critical scheduling']
      },
      'loading-unloading': { 
        fullDescription: 'Our vehicles are equipped with tail lifts and pump trucks, enabling flexible loading and unloading at any location.', 
        features: ['Tail lift equipped vehicles', 'Pump truck availability', 'No ramp required', 'Heavy item handling', 'Motorcycle loading capability', 'Furniture and equipment', 'Industrial goods', 'Trained loading crews']
      }
    }
  };

  // Build content from API or defaults
  const content = pageContent ? {
    ...defaultContent,
    heroSubtitle: language === 'bg' ? (pageContent.hero_subtitle_bg || defaultContent.heroSubtitle) : (pageContent.hero_subtitle_en || defaultContent.heroSubtitle),
    heroTitle: language === 'bg' ? (pageContent.hero_title_bg || defaultContent.heroTitle) : (pageContent.hero_title_en || defaultContent.heroTitle),
    heroTitleHighlight: language === 'bg' ? (pageContent.hero_title_highlight_bg || defaultContent.heroTitleHighlight) : (pageContent.hero_title_highlight_en || defaultContent.heroTitleHighlight),
    heroDescription: language === 'bg' ? (pageContent.hero_description_bg || defaultContent.heroDescription) : (pageContent.hero_description_en || defaultContent.heroDescription),
    ctaTitle: language === 'bg' ? (pageContent.cta_title_bg || defaultContent.ctaTitle) : (pageContent.cta_title_en || defaultContent.ctaTitle),
    ctaText: language === 'bg' ? (pageContent.cta_text_bg || defaultContent.ctaText) : (pageContent.cta_text_en || defaultContent.ctaText)
  } : defaultContent;

  // Fallback images for services without custom images
  const defaultServiceImages = {
    'international-moving': TRUCK_IMAGES.hero,
    'personal-belongings': TRUCK_IMAGES.snow,
    'express-transportation': TRUCK_IMAGES.port,
    'groupage-line': TRUCK_IMAGES.hero,
    'specialized-cargo': TRUCK_IMAGES.snow,
    'loading-unloading': TRUCK_IMAGES.port
  };

  // Helper to get service image (database image_url or fallback)
  const getServiceImage = (service) => {
    if (service.image_url && service.image_url.trim() !== '') {
      return service.image_url;
    }
    return defaultServiceImages[service.id] || TRUCK_IMAGES.hero;
  };

  // Set page title
  usePageTitle('services');

  useEffect(() => {
    const fetchData = async () => { 
      try { 
        const [servicesRes, contentRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/services-page-content`)
        ]);
        setServices(servicesRes.data.services);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) { 
        console.error('Error:', error); 
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (location.hash) { 
      const id = location.hash.replace('#', ''); 
      setTimeout(() => { 
        const element = document.getElementById(id); 
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' }); 
      }, 100); 
    }
  }, [location, services]);

  // Initialize scroll animations
  useInitScrollAnimations();

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="services-page" className="pt-20 bg-slate-50">
      <SEO page="services" />
      {/* Hero */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 scroll-animate">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 scroll-stagger">
            {services.map((service, index) => { 
              const IconComponent = iconMap[service.icon] || Truck;
              const title = language === 'bg' && service.title_bg ? service.title_bg : (service.title_en || service.title);
              return (
                <a key={service.id} href={`#${service.id}`} className="flex flex-col items-center gap-2 p-4 rounded-2xl hover:bg-white hover:shadow-lg transition-all group scroll-animate" style={{ transitionDelay: `${index * 0.05}s` }}>
                  <IconComponent className="w-6 h-6 text-[#C41E3A] group-hover:scale-110 transition-transform" />
                  <span className="text-xs text-center text-slate-500 group-hover:text-slate-900 transition-colors">{title}</span>
                </a>
              ); 
            })}
          </div>
        </div>
      </section>

      {/* Service Details */}
      {services.map((service, index) => {
        const IconComponent = iconMap[service.icon] || Truck;
        const details = content.serviceDetails[service.id] || {};
        const isEven = index % 2 === 0;
        const title = language === 'bg' && service.title_bg ? service.title_bg : (service.title_en || service.title);
        return (
          <section key={service.id} id={service.id} data-testid={`service-section-${service.id}`} className={`section-padding ${isEven ? 'bg-white' : 'bg-slate-50'}`}>
            <div className="max-w-7xl mx-auto px-4 md:px-8">
              <div className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven && 'lg:flex-row-reverse'}`}>
                <div className={!isEven ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#C41E3A]" />
                    </div>
                    <span className="overline">{title}</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{title}</h2>
                  <p className="text-slate-500 mb-8 leading-relaxed">{details.fullDescription || service.short_description}</p>
                  {details.features && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                      {details.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#C41E3A] flex-shrink-0" />
                          <span className="text-sm text-slate-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Link to="/contact?quote=true#quote-form">
                    <button data-testid={`service-quote-btn-${service.id}`} className="btn-modern btn-primary btn-shimmer">
                      {content.requestQuote} <ArrowRight className="w-4 h-4" />
                    </button>
                  </Link>
                </div>
                <div className={!isEven ? 'lg:order-1' : ''}>
                  <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
                    <img src={getServiceImage(service)} alt={title} className="w-full h-full object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact?quote=true">
              <button data-testid="services-quote-btn" className="btn-modern btn-light btn-lg">
                {content.getQuote}
              </button>
            </Link>
            <Link to="/contact">
              <button data-testid="services-contact-btn" className="btn-modern btn-lg" style={{ background: 'transparent', color: 'white', border: '2px solid rgba(255,255,255,0.5)' }}>
                {content.talkToExpert}
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
